import {
  DataGridWithCheckbox,
  Flex,
  type GridRowSelectionModel,
  NoRowsOverlay,
} from '@shared/components';
import {
  OperatorType,
  OrderType,
  useSearchUsersForAddToAppRoleQuery,
} from '@user-management/graphql';
import { useMemo, useState } from 'react';

import { RoleUserAdditionListSearchInput } from '../../components';

import { columns } from './columns';
import { mapRoleUserAdditionListItem } from './mapper';

interface RoleUserAdditionListProps {
  onSelectRow?: (selectedUsers: string[]) => void;
  appRoleId: string;
}

export default function RoleUserAdditionList(props: Readonly<RoleUserAdditionListProps>) {
  const { onSelectRow, appRoleId } = props;

  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data: userData, loading: isLoading } = useSearchUsersForAddToAppRoleQuery({
    variables: {
      paging: {
        pageSize,
        currentPage: page,
      },
      order: {
        field: 'firstname',
        type: OrderType.Asc,
      },
      search: {
        value: searchInput,
        operator: OperatorType.Or,
      },
      appRoleId,
      invertRoleFilter: true,
    },
  });

  const rows = useMemo(() => {
    return userData?.getUserAppRoleView.users
      ? mapRoleUserAdditionListItem(userData.getUserAppRoleView.users)
      : [];
  }, [userData]);

  const total = useMemo(() => {
    return userData?.getUserAppRoleView.total ? userData.getUserAppRoleView.total : 0;
  }, [userData]);

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    setPage(0);
  };

  const handleRowSelectionChange = (rowSelectionModel: GridRowSelectionModel) => {
    const selectedUsers = rowSelectionModel as string[];
    onSelectRow?.(selectedUsers);
  };

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        gap: (theme) => theme.spacing(6),
      }}
    >
      <RoleUserAdditionListSearchInput onChange={handleSearchInputChange} />
      <DataGridWithCheckbox
        columns={columns}
        rows={rows}
        loading={isLoading}
        hideFooter={isLoading}
        disableColumnMenu
        onRowSelectionModelChange={handleRowSelectionChange}
        slots={{
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          footer: {
            total: total,
            pagination: {
              page: page,
              count: total,
              rowsPerPage: pageSize,
              onPageChange: (_event, page) => {
                setPage(page);
              },
              onRowsPerPageChange: (event) => {
                const pageSize = parseInt(event.target.value);
                setPage(0);
                return setPageSize(pageSize);
              },
            },
          },
        }}
        sx={(theme) => ({
          backgroundColor: theme.palette.common.white,
          '--DataGrid-overlayHeight': '412px',
          '& .MuiDataGrid-cell': {
            padding: theme.spacing(3),
          },
          '.MuiDataGrid-columnHeaders': {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
          },
          '.MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
          },
        })}
      />
    </Flex>
  );
}
